import { and, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { AccountTransactionExtension as AccountTransactionModel } from 'tio-data/data/resources/account-transaction.ext';
import type { MatchParticipantExtension as MatchParticipantModel } from 'tio-data/data/resources/match-participant.ext';
import type { MatchPlanExtension as MatchPlanModel } from 'tio-data/data/resources/match-plan.ext';
import type { MatchPayment as MatchPaymentModel } from 'tio-data/data/resources/match-payment.type';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import SyfDashboardInsights from './dashboard/insights';
import SyfDashboardPersonalEmail from './dashboard/personal-email';
import SyfDashboardMatchSummary from './dashboard/match-summary';
import SyfDashboardLoanPayments from './dashboard/loan-payments';
import Checkbox from 'tio-ui/components/checkbox';
import Switch from 'tio-ui/components/switch';
import Select from 'tio-ui/components/select';
import PageHero from 'tio-ui/components/page-hero';
import { PhPiggyBank } from 'ember-phosphor-icons';
import { Section, VStack } from 'tio-ui/components/layout';

interface SyfDashboardSignature {
  Args: {
    accounts: AccountModel[];
    certifiedPayments: MatchPaymentModel[];
    qualifiedPayments: MatchPaymentModel[];
    eligiblePayments: AccountTransactionModel[];
    unverifiedPayments: AccountTransactionModel[];
    unqualifiedPayments: MatchPaymentModel[];
    ineligiblePayments: AccountTransactionModel[];
    onCertifyPayments: (payments: Array<MatchPaymentModel>) => Promise<void>;
    getPaymentQualificationState: (txn: AccountTransactionModel) => string;
    getPaymentCertificationState: (txn: AccountTransactionModel) => string;
    matchParticipant: MatchParticipantModel;
    matchPlan: MatchPlanModel;
    focusedAccount: AccountModel | null;
    showAccountVerifyAlert: boolean;
    showAccountUnverifyAlert: boolean;
    onToggleAccountVerified: (account: AccountModel) => void;
    onCancelAccountIsVerified: () => void;
    accountBorrowerConfirmed: boolean;
    onToggleAccountBorrowerConfirmed: () => void;
    onConfirmAccountIsVerified: (account: AccountModel) => void;
  };
}

interface BenefitYearSelectorSignature {
  Args: {
    matchPlan: MatchPlanModel;
  };
}

type YearOption = { key: string; label: string };

class BenefitYearSelector extends Component<BenefitYearSelectorSignature> {
  @service declare router: RouterService;

  get currentYear() {
    return new Date().getFullYear().toString();
  }

  get selectedYear() {
    const yearFromQuery = this.router.currentRoute?.queryParams?.benefitYear;
    if (typeof yearFromQuery === 'string') {
      return yearFromQuery;
    }
    return this.currentYear;
  }

  get availableYears(): YearOption[] {
    const benefitYears = this.args.matchPlan?.metadata?.benefit_years || {};
    const metadataYears = Object.keys(benefitYears);

    // Always include the current year, even if not yet in metadata
    const currentYearStr = this.currentYear;
    const yearsSet = new Set([currentYearStr, ...metadataYears]);
    const years = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));

    return years.map((year) => ({ key: year, label: year }));
  }

  get previousYears(): YearOption[] {
    return this.availableYears.filter((year) => year.key !== this.currentYear);
  }

  get hasPreviousYears() {
    return this.previousYears.length > 0;
  }

  yearDidChange = (selectedKey: string) => {
    this.router.transitionTo({ queryParams: { benefitYear: selectedKey } });
  };

  <template>
    <div class="tio-text-xs-medium">{{t "syf.dashboard.benefit_year"}}</div>
    <div class="tio-text-2xl-bold" data-test-benefit-year>{{this.selectedYear}}</div>
    {{#if this.hasPreviousYears}}
      <Select
        @label={{t "syf.dashboard.select_year"}}
        @items={{this.availableYears}}
        @selected={{this.selectedYear}}
        @onChange={{this.yearDidChange}}
        class="w-32 mt-2"
      />
    {{/if}}
  </template>
}

const SyfDashboardComponent: TOC<SyfDashboardSignature> = <template>
  <VStack class="gap-8">
    <PageHero
      @icon={{PhPiggyBank}}
      @title={{t "syf.default"}}
      @gradient="brand-soft"
      class="tio-elevation-card-raised"
    >
      <:stat>
        <BenefitYearSelector @matchPlan={{@matchPlan}} />
      </:stat>
    </PageHero>

    <SyfDashboardMatchSummary
      @qualifiedPayments={{@qualifiedPayments}}
      @certifiedPayments={{@certifiedPayments}}
      @unqualifiedPayments={{@unqualifiedPayments}}
      @matchPlan={{@matchPlan}}
      @onCertifyPayments={{@onCertifyPayments}}
    />

    <SyfDashboardLoanPayments
      @eligiblePayments={{@eligiblePayments}}
      @ineligiblePayments={{@ineligiblePayments}}
      @unverifiedPayments={{@unverifiedPayments}}
      @getPaymentCertificationState={{@getPaymentCertificationState}}
    />

    <Section>
      <:header>{{t "syf.dashboard.loan_accounts.header"}}</:header>
      <:body>
        <p class="mb-6">{{t "syf.dashboard.loan_accounts.main_content"}}</p>
        <div class="grid grid-cols-2">
          <div class="col-span-1 font-semibold text-center">
            {{t "syf.dashboard.loan_accounts.account_col_header"}}
          </div>
          <div class="col-span-1 font-semibold text-center">
            {{t "syf.dashboard.loan_accounts.verified_col_header"}}
          </div>
        </div>
        {{#each @accounts as |account|}}
          <div class="grid grid-cols-2">
            <div class="col-span-1">
              {{account.institutionName}}
            </div>
            <div class="col-span-1 text-center">
              <Switch
                data-test-id="account-verified-switch"
                @checked={{account.isVerified}}
                @onChange={{fn @onToggleAccountVerified account}}
              />
            </div>
          </div>
          {{! render alert here in order to keep it inside the each loop
        closure so the account var is in scope }}
          <ConfirmationDialog
            @isOpen={{and (eq @focusedAccount.id account.id) @showAccountVerifyAlert}}
            @title={{t "syf.dashboard.loan_accounts.confirmation_alert.verify.header"}}
            @intent="warning"
            @onConfirm={{fn @onConfirmAccountIsVerified account}}
            @onCancel={{@onCancelAccountIsVerified}}
            @isRunning={{account.isSaving}}
          >
            <p class="mb-6">
              {{t "syf.dashboard.loan_accounts.confirmation_alert.verify.content"}}
            </p>
            <Checkbox
              {{! font-semibold class here doesn't seem to apply to the label
            text in this Frontile component }}
              data-test-id="borrower-confirmed-checkbox"
              class="inline-block m-auto font-semibold"
              @label={{or
                @matchPlan.verifyAccountStatement
                (t "syf.shared.match_plan.default_verify_statement")
              }}
              @checked={{@accountBorrowerConfirmed}}
              @onChange={{@onToggleAccountBorrowerConfirmed}}
            />
          </ConfirmationDialog>
          <ConfirmationDialog
            @isOpen={{and (eq @focusedAccount.id account.id) @showAccountUnverifyAlert}}
            @title={{t "syf.dashboard.loan_accounts.confirmation_alert.unverify.header"}}
            @intent="warning"
            @onConfirm={{fn @onConfirmAccountIsVerified account}}
            @onCancel={{@onCancelAccountIsVerified}}
            @isRunning={{account.isSaving}}
          >
            <p class="mb-6">
              {{t "syf.dashboard.loan_accounts.confirmation_alert.unverify.content"}}
            </p>
          </ConfirmationDialog>
        {{/each}}
      </:body>
    </Section>

    {{#if @matchPlan.showInsights}}
      <SyfDashboardInsights
        @matchParticipant={{@matchParticipant}}
        @matchPlan={{@matchPlan}}
        @accounts={{@accounts}}
      />
    {{/if}}

    <SyfDashboardPersonalEmail @matchParticipant={{@matchParticipant}} />
  </VStack>
</template>;

export default SyfDashboardComponent;
